// EntityListMobile.tsx
import React, { useState } from 'react';
import { 
    Box, Button, Stack, Typography, Card, CardContent, 
    Divider, IconButton, useMediaQuery, useTheme 
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Edit, Delete } from '@mui/icons-material';
import { DataGrid, GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { api } from "../../../api/entityApi";
import { useFetch } from "../../../hooks/useFetch";
import { usePresetDialogs } from '../../dialog';
import { FormModal } from '../../modal/FormModal';
import ActionButtons from '../../ActionButtons';
import { Entity } from '../../../types/entity';
import EntityForm from './EntityForm';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LocalizedGrid from '../../localizedGrid/LocalizedGridCook3';
import i18n from '../../../i18n';
import { useAuthStore } from '../../../store/useAuthStore3';

export const EntityList: React.FC<{type: number}> = ({ type }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const { data, setData, error } = useFetch<Entity[]>(api.get, [], { type: type });
    const { data: parents } = useFetch<Entity[]>(api.getParents, [], { type: type - 1 });
    const { customerId, isConfirmed, isEmailConfirmed } = useAuthStore();
    const { showInfo, showConfirm, DialogComponent } = usePresetDialogs();
    const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();

    if (!customerId) {
        return <Typography>{t('customer.loading')}</Typography>;
    }    

    const columns_0: GridColDef[] = [
        { field: "entity_id", headerName: "ID", type: "number", flex: 1, width: 50 },
        { field: "name", headerName: t('Entity.name'), type: "string", flex: 2, width: 200 },
        { 
            field: "parent_id", 
            headerName: t('Entity.owner'), 
            type: "string", 
            flex: 2, 
            width: 200,
            renderCell: (params: GridRenderCellParams<Entity>) => (
                 <span style={{ lineHeight: 'normal', alignSelf: 'center' }}>
                    {parents.find(p => p.entity_id === params.row.parent_id)?.name}
                </span>
            )
        },
        {
            field: 'actions',
            headerName: t('common.actions'), // Исправлена опечатка 'coomon'
            width: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams<Entity>) => (
                <ActionButtons actions={[
                    {type: 'edit', action: () => handleOpenModal(params.row) },
                    {type: 'delete', action: () => handleDelete(params.row.entity_id) },
                ]} />
            ),
        },        
    ];

    const columns = type === 1 ? columns_0.filter(col => col.field !== 'parent_id') : columns_0;

    // --- Handlers ---
    const handleUpdate = async (updateData: Entity) => {
        if (selectedEntity) {
            await api.put(updateData);
            setData((prev) => prev.map(entity => entity.entity_id === updateData.entity_id ? updateData : entity));
        } else {
            const newData = await api.post(updateData);
            setData((prev) => [...prev, newData]);
        }
        handleCloseModal();
    };

    const handleDelete = async (id: number) => {
        const result = await showConfirm(t('common.delete_question', {id}), t('common.delete_confirmation'));
        if (!result) return;
        await api.delete(id);
        setData(prev => prev.filter(entity => id !== entity.entity_id));
    };
    
    const handleOpenModal = (entity: Entity | null) => {
        if (!isConfirmed) {
            showInfo('Потрібне підтвердження адреси електронної пошти', 'Попередження');
            return;
        }
        setSelectedEntity(entity);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);

    const handleNavigate = () => {
        navigate(type === 1 ? '/checkpoints' : '/origins');
    }

    // --- Mobile Card Render ---
    const renderMobileCard = (entity: Entity) => (
        <Card key={entity.entity_id} sx={{ mb: 2, boxShadow: 1 }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {entity.name}
                    </Typography>
                    <Box>
                        <IconButton onClick={() => handleOpenModal(entity)} size="small"><Edit /></IconButton>
                        <IconButton onClick={() => handleDelete(entity.entity_id)} size="small" color="error"><Delete /></IconButton>
                    </Box>
                </Box>
                {type !== 1 && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2">
                            <strong>{t('Entity.owner')}:</strong> {parents.find(p => p.entity_id === entity.parent_id)?.name || '—'}
                        </Typography>
                    </>
                )}
                <Typography variant="caption" color="text.secondary">ID: {entity.entity_id}</Typography>
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{ width: '100%', p: isMobile ? 1 : 0 }}>
            {DialogComponent}

            {/* Верхняя панель управления */}
            <Stack 
                direction={isMobile ? "column" : "row"} 
                spacing={isMobile ? 1 : 2} 
                sx={{ mb: 3, alignItems: isMobile ? 'stretch' : 'center' }}
            >
                <Button 
                    fullWidth={isMobile}
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenModal(null)}
                >
                    {type === 2 ? t('Entity.add_new_checkpoint') : t('Entity.add_new_point')}
                </Button>
                
                <Stack direction="row" spacing={1} alignItems="center" justifyContent={isMobile ? "center" : "flex-start"}>
                    <Typography variant="body2" color="text.secondary">
                        ({t('EntityList.NextStep')})
                    </Typography>
                    <Typography
                        onClick={handleNavigate}
                        sx={{
                            cursor: 'pointer',
                            color: 'primary.main',
                            textDecoration: 'underline',
                            fontSize: '0.875rem',
                            '&:hover': { textDecoration: 'none' },
                        }}
                    >
                        {type === 2 ? t('EntityList.navigateOrigins') : t('EntityList.navigateCheckpoints')}
                    </Typography>
                </Stack>
            </Stack>

            {/* Контент: Таблица или Карточки */}
            {isMobile ? (
                <Box>
                    {data.map(entity => renderMobileCard(entity))}
                </Box>
            ) : (
                <Box sx={{ height: 500, width: '100%' }}>
                    {/* <DataGrid<Entity> */}
                    <LocalizedGrid
                        lang={i18n.language as any}
                        gridId='EntityListMobile'
                        rows={data}
                        columns={columns} 
                        getRowId={(row) => row.entity_id}
                        // sx={{ maxWidth: '800px' }}
                        // autoHeight
                        sx={{ 
                            border: 'none', 
                            height: 'calc(100vh - 340px)' // Вычитаем высоту хедера и отступов
                        }}
                        initialState={{
                            columns: {columnVisibilityModel: {id: false}}
                        }}
                        hideToolbar={true}
                    />
                </Box>
            )}

            <Typography color='error'>{error?.message}</Typography>

            <FormModal open={isModalOpen} onClose={handleCloseModal}>
                <EntityForm
                    entity={selectedEntity}
                    type={type}
                    parents={parents}
                    onSave={handleUpdate}
                    onCancel={handleCloseModal}
                />
            </FormModal>
        </Box>
    );
};